import React, { useState, useMemo } from 'react';
import { usePagination } from './usePagination';

export type SortDirection = 'asc' | 'desc';

export interface TableConfig<T, K extends keyof T> {
  data: T[];
  defaultSortField: K;
  defaultSortDirection?: SortDirection;
  itemsPerPage?: number;
  sortValueExtractor?: (item: T, field: K) => any;
}

export interface TableState<T, K extends keyof T> {
  // Sorting
  sortField: K;
  sortDirection: SortDirection;
  handleSort: (field: K) => void;
  getSortLabel: (field: K) => string;

  // Data
  sortedData: T[];

  // Pagination
  pagination: ReturnType<typeof usePagination<T>>;

  // UI helpers
  getSortIcon: (field: K) => React.ReactNode;
  sortInfo: {
    field: K;
    direction: SortDirection;
    label: string;
  };
}

/**
 * Hook para manejar paginación y ordenamiento de tablas de forma reutilizable
 *
 * @param config - Configuración de la tabla
 * @param fieldLabels - Mapeo de campos a etiquetas legibles
 * @returns Estado completo de la tabla con paginación y ordenamiento
 */
export function useTableData<T, K extends keyof T>(
  config: TableConfig<T, K>,
  fieldLabels: Record<K, string>
): TableState<T, K> {
  const {
    data,
    defaultSortField,
    defaultSortDirection = 'asc',
    itemsPerPage = 10,
    sortValueExtractor
  } = config;

  const [sortField, setSortField] = useState<K>(defaultSortField);
  const [sortDirection, setSortDirection] = useState<SortDirection>(defaultSortDirection);

  const handleSort = (field: K) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortLabel = (field: K) => fieldLabels[field] || String(field);

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      if (sortValueExtractor) {
        aValue = sortValueExtractor(a, sortField);
        bValue = sortValueExtractor(b, sortField);
      } else {
        aValue = a[sortField];
        bValue = b[sortField];
      }

      // Manejo de strings
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Manejo de fechas
      if (aValue instanceof Date && bValue instanceof Date) {
        aValue = aValue.getTime();
        bValue = bValue.getTime();
      }

      // Manejo de strings que pueden ser fechas
      if (typeof aValue === 'string' && !isNaN(Date.parse(aValue))) {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  }, [data, sortField, sortDirection, sortValueExtractor]);

  const pagination = usePagination({
    data: sortedData,
    itemsPerPage
  });

  const getSortIcon = (field: K): React.ReactNode => {
    if (typeof window === 'undefined') return null;

    const ChevronUp = () => (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    );

    const ChevronDown = () => (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );

    if (sortField !== field) {
      return <ChevronUp className="text-gray-400" />;
    }
    return sortDirection === 'asc'
      ? <ChevronUp className="text-gray-600" />
      : <ChevronDown className="text-gray-600" />;
  };

  const sortInfo = {
    field: sortField,
    direction: sortDirection,
    label: getSortLabel(sortField)
  };

  return {
    sortField,
    sortDirection,
    handleSort,
    getSortLabel,
    sortedData,
    pagination,
    getSortIcon,
    sortInfo
  };
}